import React from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import TextAlign from '@tiptap/extension-text-align';
import { Color } from '@tiptap/extension-color';
import { TextStyle } from '@tiptap/extension-text-style';
import Placeholder from '@tiptap/extension-placeholder';
import {
  Bold, Italic, Underline as UnderlineIcon, Strikethrough,
  List, ListOrdered, AlignLeft, AlignCenter, AlignRight,
  Link as LinkIcon, Undo, Redo, Image as ImageIcon,
} from 'lucide-react';

const ToolbarButton = ({ onClick, active, children, title }) => (
  <button
    type="button"
    onClick={onClick}
    title={title}
    className={`p-1.5 rounded hover:bg-slate-200 transition-colors ${active ? 'bg-slate-200 text-blue-600' : 'text-slate-600'}`}
  >
    {children}
  </button>
);

// Resize + compress an image File to keep email bodies small. Otherwise a
// pasted 1920×1080 screenshot lands as a 2-4 MB base64 blob inside body_html
// which (1) the proxy rejects with 413 and (2) the recipient sees at full
// resolution. We cap at 1024 px wide and re-encode to JPEG at quality 0.82.
// Result: typical screenshots end up around 60-120 KB.
const MAX_INLINE_IMG_WIDTH = 1024;
const INLINE_IMG_QUALITY = 0.82;
const MAX_INLINE_IMG_BYTES = 2 * 1024 * 1024; // 2 MB hard cap on the final payload

const _readFileAsDataURL = (file) =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = (e) => resolve(e.target.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });

const _loadImage = (src) =>
  new Promise((resolve, reject) => {
    const img = new window.Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });

const _resizeImageDataUrl = async (file) => {
  try {
    const original = await _readFileAsDataURL(file);
    const img = await _loadImage(original);
    const ratio = img.width > MAX_INLINE_IMG_WIDTH ? MAX_INLINE_IMG_WIDTH / img.width : 1;
    const w = Math.round(img.width * ratio);
    const h = Math.round(img.height * ratio);
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, 0, 0, w, h);
    // PNGs with transparency lose alpha when forced to JPEG. For screenshots
    // (the user's case) JPEG is fine — much smaller. We only keep the PNG
    // re-encoded if the original was a small PNG with alpha (<200 KB).
    const isPng = file.type === 'image/png';
    const fmt = isPng && file.size < 200 * 1024 ? 'image/png' : 'image/jpeg';
    const dataUrl = canvas.toDataURL(fmt, INLINE_IMG_QUALITY);
    return dataUrl;
  } catch (err) {
    // If anything goes wrong, fall back to the original (user gets the old
    // behaviour rather than nothing).
    console.warn('Image resize failed, using original:', err);
    return _readFileAsDataURL(file);
  }
};

const _approximateBytes = (dataUrl) => {
  // base64 over data URL: payload length ≈ 3/4 of base64 length.
  const idx = dataUrl.indexOf(',');
  const b64 = idx >= 0 ? dataUrl.slice(idx + 1) : dataUrl;
  return Math.floor(b64.length * 0.75);
};

const _insertResizedImage = async (file, view, dropEvent = null) => {
  const dataUrl = await _resizeImageDataUrl(file);
  if (_approximateBytes(dataUrl) > MAX_INLINE_IMG_BYTES) {
    try {
      // Lazy import sonner to avoid breaking when not installed yet.
      const { toast } = await import('sonner');
      toast.error('Imagen demasiado grande tras compresión. Súbela como adjunto.');
    } catch (_) {
      console.warn('Image exceeds 2MB after compression, dropping.');
    }
    return;
  }
  const { schema } = view.state;
  const node = schema.nodes.image.create({ src: dataUrl });
  let tr;
  if (dropEvent) {
    const pos = view.posAtCoords({ left: dropEvent.clientX, top: dropEvent.clientY });
    tr = pos ? view.state.tr.insert(pos.pos, node) : view.state.tr.replaceSelectionWith(node);
  } else {
    tr = view.state.tr.replaceSelectionWith(node);
  }
  view.dispatch(tr);
};

const RichTextEditor = ({ value, onChange, placeholder, minHeight = '150px', maxHeight = '350px' }) => {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      TextStyle,
      Color,
      Link.configure({ openOnClick: false }),
      Image.configure({
        inline: true,
        allowBase64: true,
        HTMLAttributes: {
          style: 'max-width: 100%; height: auto; border-radius: 4px;',
        },
      }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Placeholder.configure({ placeholder: placeholder || '' }),
    ],
    content: value || '',
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: 'prose prose-sm max-w-none focus:outline-none px-3 py-2',
        // Cap the editor height so pasting a huge block doesn't push the
        // dialog content (Send button etc.) out of the viewport. The editor
        // gets its own internal scrollbar instead.
        style: `min-height: ${minHeight}; max-height: ${maxHeight}; overflow-y: auto; word-wrap: break-word; overflow-wrap: anywhere;`,
      },
      handlePaste(view, event) {
        const items = event.clipboardData?.items;
        if (!items) return false;
        for (const item of items) {
          if (item.type.startsWith('image/')) {
            const file = item.getAsFile();
            if (file) {
              _insertResizedImage(file, view);
              return true;
            }
          }
        }
        return false;
      },
      handleDrop(view, event) {
        const files = event.dataTransfer?.files;
        if (!files || files.length === 0) return false;
        for (const file of files) {
          if (file.type.startsWith('image/')) {
            event.preventDefault();
            _insertResizedImage(file, view, event);
            return true;
          }
        }
        return false;
      },
    },
  });

  React.useEffect(() => {
    if (!editor) return;
    const currentHTML = editor.getHTML();
    if (value !== currentHTML && value !== undefined) {
      const isPlainText = !value.includes('<') || value === '<p></p>';
      if (isPlainText && value !== '') {
        const wrapped = value.split('\n').map(line => `<p>${line || '<br>'}</p>`).join('');
        if (wrapped !== currentHTML) {
          editor.commands.setContent(wrapped);
        }
      } else if (value !== currentHTML) {
        editor.commands.setContent(value);
      }
    }
  }, [value, editor]);

  if (!editor) return null;

  const addLink = () => {
    const url = window.prompt('URL:');
    if (url) {
      editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    }
  };

  const addImage = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      // Apply the same resize+compress pipeline used for paste/drop so file
      // uploads from the toolbar don't bloat body_html either.
      const dataUrl = await _resizeImageDataUrl(file);
      if (_approximateBytes(dataUrl) > MAX_INLINE_IMG_BYTES) {
        try {
          const { toast } = await import('sonner');
          toast.error('Imagen demasiado grande tras compresión. Súbela como adjunto.');
        } catch (_) {
          // ignore
        }
        return;
      }
      editor.chain().focus().setImage({ src: dataUrl }).run();
    };
    input.click();
  };

  return (
    <div className="border rounded-lg overflow-hidden bg-white">
      <div className="flex flex-wrap items-center gap-0.5 px-2 py-1 border-b bg-slate-50">
        <ToolbarButton onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive('bold')} title="Negrita">
          <Bold className="h-3.5 w-3.5" />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive('italic')} title="Cursiva">
          <Italic className="h-3.5 w-3.5" />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive('underline')} title="Subrayado">
          <UnderlineIcon className="h-3.5 w-3.5" />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleStrike().run()} active={editor.isActive('strike')} title="Tachado">
          <Strikethrough className="h-3.5 w-3.5" />
        </ToolbarButton>

        <div className="w-px h-5 bg-slate-300 mx-1" />

        <ToolbarButton onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive('bulletList')} title="Lista">
          <List className="h-3.5 w-3.5" />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive('orderedList')} title="Lista numerada">
          <ListOrdered className="h-3.5 w-3.5" />
        </ToolbarButton>

        <div className="w-px h-5 bg-slate-300 mx-1" />

        <ToolbarButton onClick={() => editor.chain().focus().setTextAlign('left').run()} active={editor.isActive({ textAlign: 'left' })} title="Izquierda">
          <AlignLeft className="h-3.5 w-3.5" />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().setTextAlign('center').run()} active={editor.isActive({ textAlign: 'center' })} title="Centro">
          <AlignCenter className="h-3.5 w-3.5" />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().setTextAlign('right').run()} active={editor.isActive({ textAlign: 'right' })} title="Derecha">
          <AlignRight className="h-3.5 w-3.5" />
        </ToolbarButton>

        <div className="w-px h-5 bg-slate-300 mx-1" />

        <ToolbarButton onClick={addLink} active={editor.isActive('link')} title="Enlace">
          <LinkIcon className="h-3.5 w-3.5" />
        </ToolbarButton>

        <ToolbarButton onClick={addImage} title="Insertar imagen (también puedes pegar con Ctrl+V)">
          <ImageIcon className="h-3.5 w-3.5" />
        </ToolbarButton>

        <select
          onChange={(e) => {
            const size = e.target.value;
            if (size === 'normal') {
              editor.chain().focus().setParagraph().run();
            } else {
              editor.chain().focus().toggleHeading({ level: parseInt(size) }).run();
            }
          }}
          className="h-7 text-xs border rounded px-1 bg-white ml-1"
          value={
            editor.isActive('heading', { level: 1 }) ? '1' :
            editor.isActive('heading', { level: 2 }) ? '2' :
            editor.isActive('heading', { level: 3 }) ? '3' : 'normal'
          }
        >
          <option value="normal">Normal</option>
          <option value="1">H1</option>
          <option value="2">H2</option>
          <option value="3">H3</option>
        </select>

        <div className="w-px h-5 bg-slate-300 mx-1" />

        <ToolbarButton onClick={() => editor.chain().focus().undo().run()} title="Deshacer">
          <Undo className="h-3.5 w-3.5" />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().redo().run()} title="Rehacer">
          <Redo className="h-3.5 w-3.5" />
        </ToolbarButton>
      </div>

      <EditorContent editor={editor} />
      <style>{`
        .ProseMirror ul { list-style-type: disc; padding-left: 1.5em; margin: 0.5em 0; }
        .ProseMirror ol { list-style-type: decimal; padding-left: 1.5em; margin: 0.5em 0; }
        .ProseMirror li { margin: 0.2em 0; }
        .ProseMirror h1 { font-size: 1.5em; font-weight: bold; margin: 0.5em 0; }
        .ProseMirror h2 { font-size: 1.25em; font-weight: bold; margin: 0.5em 0; }
        .ProseMirror h3 { font-size: 1.1em; font-weight: bold; margin: 0.5em 0; }
        .ProseMirror a { color: #2563eb; text-decoration: underline; }
        .ProseMirror img { max-width: 100%; height: auto; border-radius: 4px; margin: 0.25em 0; }
        .ProseMirror p.is-editor-empty:first-child::before {
          content: attr(data-placeholder);
          float: left;
          color: #9ca3af;
          pointer-events: none;
          height: 0;
        }
      `}</style>
    </div>
  );
};

export default RichTextEditor;
